/**
 * SubscriptionSuccessScreen
 * Shown after successful Stripe checkout
 * Welcomes user to premium and shows unlocked features
 */

import React, { useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Platform,
  Animated,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import Button from '../components/common/Button';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import { salesApi } from '../api/endpoints/sales';
import logger from '../utils/logger';

// Features unlocked with premium
const UNLOCKED_FEATURES = [
  {
    icon: 'infinity',
    title: 'Messages illimités',
    description: 'Discute avec Wealfi sans aucune limite',
  },
  {
    icon: 'lightning-bolt',
    title: 'Détection d\'arbitrage',
    description: 'Reçois des alertes sur les opportunités d\'optimisation',
  },
  {
    icon: 'chart-timeline-variant',
    title: 'Simulateur d\'investissement',
    description: 'Projections personnalisées PEA, SCPI, Assurance-vie',
  },
  {
    icon: 'bell-ring',
    title: 'Alertes personnalisées',
    description: 'Suis tes objectifs en temps réel',
  },
  {
    icon: 'calendar-check',
    title: 'Check-in hebdomadaire',
    description: 'Bilan et conseils chaque dimanche',
  },
  {
    icon: 'shield-check',
    title: 'Support prioritaire',
    description: 'Assistance rapide pour toutes tes questions',
  },
];

export default function SubscriptionSuccessScreen() {
  const navigation = useNavigation();
  const route = useRoute();

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.8)).current;
  const slideAnim = useRef(new Animated.Value(30)).current;

  // Get session_id from URL params if available (for web)
  const sessionId = (route.params as any)?.session_id;

  useEffect(() => {
    // Start entrance animation
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 500,
        useNativeDriver: true,
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        tension: 50,
        friction: 7,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 400,
        useNativeDriver: true,
      }),
    ]).start();

    // Could verify the session with backend here if needed
    if (sessionId) {
      logger.log('Stripe session completed:', sessionId);
    }

    // Track conversion for sales attribution
    salesApi.trackConversion()
      .then((result) => {
        logger.log('[SubscriptionSuccess] Conversion tracked:', result);
        if (result.attributed && result.attribution) {
          logger.log('[SubscriptionSuccess] Attribution:', result.attribution);
        }
      })
      .catch((error) => {
        logger.error('[SubscriptionSuccess] Failed to track conversion:', error);
      });
  }, [sessionId]);

  const handleGoToDashboard = () => {
    // Navigate to dashboard
    navigation.reset({
      index: 0,
      routes: [{ name: 'Main' as never }],
    });
  };

  const handleStartChat = () => {
    // Navigate directly to chat
    navigation.reset({
      index: 0,
      routes: [{ name: 'Main' as never, params: { screen: 'Chats' } }],
    });
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
    >
      {/* Success Animation/Icon */}
      <Animated.View
        style={[
          styles.successIcon,
          {
            opacity: fadeAnim,
            transform: [{ scale: scaleAnim }],
          },
        ]}
      >
        <View style={styles.checkCircle}>
          <Icon name="check" size={48} color="#FFFFFF" />
        </View>
      </Animated.View>

      {/* Welcome Message */}
      <Animated.View
        style={[
          styles.header,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <Text style={styles.title}>Bienvenue chez les Premium !</Text>
        <Text style={styles.subtitle}>
          Ton abonnement est maintenant actif. Tu as accès à toutes les
          fonctionnalités de Wealfi.
        </Text>
      </Animated.View>

      {/* Unlocked Features */}
      <View style={styles.featuresSection}>
        <Text style={styles.sectionTitle}>Ce qui est maintenant deboque :</Text>

        {UNLOCKED_FEATURES.map((feature, index) => (
          <View key={index} style={styles.featureItem}>
            <View style={styles.featureIcon}>
              <Icon name={feature.icon} size={24} color={colors.primary} />
            </View>
            <View style={styles.featureText}>
              <Text style={styles.featureTitle}>{feature.title}</Text>
              <Text style={styles.featureDescription}>
                {feature.description}
              </Text>
            </View>
            <Icon name="check-circle" size={20} color={colors.success} />
          </View>
        ))}
      </View>

      {/* Next Steps */}
      <View style={styles.nextStepsSection}>
        <Text style={styles.sectionTitle}>Prochaines etapes :</Text>

        <View style={styles.stepCard}>
          <View style={styles.stepNumber}>
            <Text style={styles.stepNumberText}>1</Text>
          </View>
          <View style={styles.stepContent}>
            <Text style={styles.stepTitle}>Parle a Wealfi</Text>
            <Text style={styles.stepDescription}>
              Pose toutes tes questions sur tes finances sans limite
            </Text>
          </View>
        </View>

        <View style={styles.stepCard}>
          <View style={styles.stepNumber}>
            <Text style={styles.stepNumberText}>2</Text>
          </View>
          <View style={styles.stepContent}>
            <Text style={styles.stepTitle}>Verifie les opportunites</Text>
            <Text style={styles.stepDescription}>
              Regarde si des arbitrages sont disponibles sur ton dashboard
            </Text>
          </View>
        </View>

        <View style={styles.stepCard}>
          <View style={styles.stepNumber}>
            <Text style={styles.stepNumberText}>3</Text>
          </View>
          <View style={styles.stepContent}>
            <Text style={styles.stepTitle}>Simule tes investissements</Text>
            <Text style={styles.stepDescription}>
              Projette tes gains sur 10 ans avec le simulateur
            </Text>
          </View>
        </View>
      </View>

      {/* CTA Buttons */}
      <View style={styles.ctaContainer}>
        <Button
          title="Parler a Wealfi"
          onPress={handleStartChat}
          style={styles.primaryButton}
        />
        <Button
          title="Retour au Dashboard"
          onPress={handleGoToDashboard}
          variant="outline"
          style={styles.secondaryButton}
        />
      </View>

      {/* Support Note */}
      <Text style={styles.supportNote}>
        Une question ? Ecris-nous a support@wealfi.ai
      </Text>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    paddingHorizontal: spacing.lg,
    paddingTop: Platform.OS === 'ios' ? 60 : 40,
    paddingBottom: spacing.xxl,
  },

  // Success Icon
  successIcon: {
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  checkCircle: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: colors.success,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: colors.success,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },

  // Header
  header: {
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  title: {
    fontSize: typography.fontSize.xxl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  subtitle: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 24,
  },

  // Features Section
  featuresSection: {
    marginBottom: spacing.xl,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.md,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  featureIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: `${colors.primary}15`,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  featureText: {
    flex: 1,
  },
  featureTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: 2,
  },
  featureDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },

  // Next Steps Section
  nextStepsSection: {
    marginBottom: spacing.xl,
  },
  stepCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: spacing.md,
  },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  stepNumberText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: 2,
  },
  stepDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    lineHeight: 20,
  },

  // CTA
  ctaContainer: {
    marginBottom: spacing.lg,
  },
  primaryButton: {
    marginBottom: spacing.sm,
  },
  secondaryButton: {},

  // Support
  supportNote: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    textAlign: 'center',
  },
});
